import React, { useEffect, useMemo, useState } from 'react';
import { Upload, Image as ImageIcon, Sparkles, Download, AlertCircle, CheckCircle, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';

const buildFileEntry = (file) => ({
  id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
  file,
  name: file.name,
  size: file.size,
  type: file.type,
  preview: URL.createObjectURL(file),
  status: 'uploaded',
  result: null,
});

const statusMeta = {
  uploaded: { label: 'Ready', className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  processing: { label: 'Processing…', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  upscaled: { label: 'Upscaled', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  error: { label: 'Failed', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

const ImageUpscale = ({ topazService }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [processingStatus, setProcessingStatus] = useState({});
  const [error, setError] = useState('');
  const [scaleFactor, setScaleFactor] = useState(2); // Default to 2x
  const isServiceReady = useMemo(() => Boolean(topazService), [topazService]);

  useEffect(() => () => {
    uploadedFiles.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [uploadedFiles]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }
    setError('');
    const newEntries = files.map(buildFileEntry);
    setUploadedFiles((prev) => [...prev, ...newEntries]);
  };

  const toggleSelection = (fileId) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const clearFile = (fileId) => {
    setUploadedFiles((prev) => {
      const remaining = prev.filter((file) => {
        const keep = file.id !== fileId;
        if (!keep) {
          URL.revokeObjectURL(file.preview);
        }
        return keep;
      });
      return remaining;
    });
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
  };

  const handleUpscaleSelected = async () => {
    if (selectedFiles.size === 0) {
      setError('Select at least one image to enhance.');
      return;
    }
    if (!isServiceReady) {
      setError('Image enhancement requires a valid Topaz API key. Add it in the header before enhancing.');
      return;
    }

    setError('');
    const selectedEntries = uploadedFiles.filter((file) => selectedFiles.has(file.id));

    for (const entry of selectedEntries) {
      setProcessingStatus((prev) => ({ ...prev, [entry.id]: 'processing' }));
      try {
        const result = await topazService.enhanceImage(entry.file, {
          scale: scaleFactor
        });
        setUploadedFiles((prev) => prev.map((file) => (file.id === entry.id ? {
          ...file,
          status: 'upscaled',
          result,
        } : file)));
        setProcessingStatus((prev) => ({ ...prev, [entry.id]: 'upscaled' }));
      } catch (err) {
        console.error('Topaz enhancement error:', err);
        setProcessingStatus((prev) => ({ ...prev, [entry.id]: 'error' }));
        setUploadedFiles((prev) => prev.map((file) => (file.id === entry.id ? {
          ...file,
          status: 'error',
          result: null,
        } : file)));
        setError(err.message || 'Image enhancement failed.');
      }
    }

    setSelectedFiles(new Set());
  };

  const handleDownload = async (file) => {
    if (!file.result?.downloadUrl) {
      return;
    }
    try {
      const response = await fetch(file.result.downloadUrl);
      if (!response.ok) {
        throw new Error('Failed to download enhanced image');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name.replace(/(\.[^.]+)?$/, '-enhanced$1');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Download failed.');
    }
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === uploadedFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(uploadedFiles.map((file) => file.id)));
    }
  };

  const renderStatusBadge = (file) => {
    const status = processingStatus[file.id] || file.status;
    const meta = statusMeta[status] || statusMeta.uploaded;
    return (
      <Badge variant="secondary" className={`px-2 py-0.5 text-xs border ${meta.className}`}>
        {meta.label}
      </Badge>
    );
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col font-display bg-background text-foreground">
      <div className="flex h-full grow flex-col">
        <div className="flex flex-1 flex-col lg:flex-row">
          <aside className="w-full lg:w-96 flex-shrink-0 bg-card lg:border-r border-border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">Image Upscale</h2>
              <Badge variant="secondary" className={`text-xs ${isServiceReady ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'}`}>
                {isServiceReady ? 'Topaz Connected' : 'API key needed'}
              </Badge>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Upload Images</label>
              <div className="relative">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-accent transition-colors">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-muted">
                    <Upload className="h-6 w-6 text-accent" />
                  </div>
                  <p className="text-sm text-foreground">Drop files here or click to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">PNG, JPG, JPEG up to 20MB each</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground">Enhancement Options</h3>

              {/* Scale Factor Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Scale Factor</label>
                <div className="grid grid-cols-3 gap-2">
                  {[2, 4, 6].map((scale) => (
                    <button
                      key={scale}
                      onClick={() => setScaleFactor(scale)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                        scaleFactor === scale
                          ? 'bg-accent text-accent-foreground border-accent shadow-sm'
                          : 'bg-background text-foreground border-border hover:border-accent/50'
                      }`}
                    >
                      {scale}x
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Topaz AI supports up to 6x maximum</p>
              </div>

              <Card className="bg-muted/40 border-border">
                <CardContent className="space-y-3 p-4 text-sm text-foreground/70">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-accent" />
                    <span>Upscale selected images up to {scaleFactor}x with Topaz AI.</span>
                  </div>
                  <p>Selections are processed sequentially to manage credits.</p>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-3">
              <Button
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={handleUpscaleSelected}
                disabled={selectedFiles.size === 0}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Enhance Selected ({selectedFiles.size})
              </Button>
              <Button
                variant="outline"
                onClick={handleSelectAll}
                className="w-full"
                disabled={uploadedFiles.length === 0}
              >
                {selectedFiles.size === uploadedFiles.length ? 'Clear Selection' : 'Select All'}
              </Button>
            </div>

            {error && (
              <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
                <AlertCircle className="mt-0.5 h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
          </aside>

          <main className="flex-1 p-6 lg:p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-foreground">Image Enhancement</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{uploadedFiles.length} image{uploadedFiles.length === 1 ? '' : 's'} uploaded</span>
                {uploadedFiles.length > 0 && (
                  <button
                    onClick={handleSelectAll}
                    className="text-accent hover:text-accent/80"
                  >
                    {selectedFiles.size === uploadedFiles.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
            </div>

            {uploadedFiles.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <ImageIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">No images uploaded yet</h3>
                  <p className="text-sm text-muted-foreground">Upload presentation imagery to generate high-resolution assets for your deck.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {uploadedFiles.map((file) => {
                  const statusKey = processingStatus[file.id] || file.status;
                  const isProcessing = statusKey === 'processing';
                  const isError = statusKey === 'error';
                  return (
                    <Card key={file.id} className="bg-card border-border overflow-hidden">
                      <div className="flex flex-col md:flex-row gap-4 p-4">
                        <div className="relative bg-muted rounded-lg flex items-center justify-center flex-shrink-0" style={{width: '280px', height: '200px'}}>
                          <img src={file.preview} alt={file.name} className="max-h-full max-w-full object-contain" />
                        <div className="absolute top-3 left-3 flex gap-2">
                          {renderStatusBadge(file)}
                          {selectedFiles.has(file.id) && (
                            <Badge variant="secondary" className="bg-accent/20 text-accent border-accent/30">
                              Selected
                            </Badge>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => clearFile(file.id)}
                          className="absolute top-3 right-3 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                          aria-label="Remove"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        {isProcessing && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                            <Loader2 className="h-6 w-6 animate-spin text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col justify-between">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-foreground">{file.name}</h3>
                              <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => clearFile(file.id)}
                              className="rounded-full hover:bg-muted p-1"
                              aria-label="Remove"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {file.result?.credits && (
                            <div className="flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle className="h-3 w-3" />
                              {file.result.credits} credits used
                            </div>
                          )}
                        </div>
                        <div className="space-y-3 mt-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedFiles.has(file.id)}
                              onChange={() => toggleSelection(file.id)}
                              className="h-4 w-4 rounded border-border bg-background text-accent focus:ring-accent"
                            />
                            <span className="text-sm text-foreground">Select for enhancement</span>
                          </div>
                          <div className="flex gap-2">
                            {file.result?.downloadUrl && (
                              <Button
                                size="sm"
                                className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
                                onClick={() => handleDownload(file)}
                              >
                                <Download className="mr-2 h-4 w-4" />
                                Download
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => toggleSelection(file.id)}
                            >
                              {selectedFiles.has(file.id) ? 'Deselect' : 'Select'}
                            </Button>
                          </div>
                          {isError && (
                            <div className="flex items-start gap-2 rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-400">
                              <AlertCircle className="mt-0.5 h-3 w-3" />
                              Enhancement failed. Try again later.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ImageUpscale;
